/**
 * Application route table.
 *
 * Two layouts:
 *   - AuthLayout — login + signup screens (no sidebar/topbar)
 *   - AppShell    — main app chrome (topbar + sidebar + main)
 *
 * Authenticated areas are wrapped in ProtectedRoute. The guard is opt-in
 * via VITE_REQUIRE_AUTH so we can ship a clickable demo before the Django
 * auth API is online.
 */
import { createBrowserRouter } from "react-router-dom";
import { AppShell } from "@/layouts/app-shell";
import { AuthLayout } from "@/layouts/auth-layout";
import { ProtectedRoute } from "@/components/app/protected-route";
import { PublicRoute } from "@/components/app/public-route";
import { OverviewPage } from "@/pages/overview";
import { DesignSystemPage } from "@/pages/design-system";
import { LoginPage } from "@/pages/login";
import { ForgotPasswordPage } from "@/pages/forgot";
import { NotFoundPage } from "@/pages/not-found";
import { PlaceholderPage } from "@/pages/placeholder";
import { MerakiWorkflowPicker } from "@/pages/meraki/workflow-picker";
import { SetMerakiKeyPage } from "./pages/meraki/set-meraki-key";
import { MerakiDebrandingPage } from "@/pages/meraki/debranding";
import { MerakiPostSignoffPage } from "@/pages/meraki/post-signoff";
import { MerakiApiSuitePage } from "@/pages/meraki/api-suite";
import { MerakiUpdateDevicesPage } from "@/pages/meraki/update-devices";
import { MerakiSwitchPortsPage } from "@/pages/meraki/switch-ports";
import { MerakiOnboardingPage } from "@/pages/meraki/onboarding";
import { MerakiProvisioningRunner } from "@/pages/meraki/provisioning-runner";
import { SetArubaKeyPage } from "@/pages/aruba/set-aruba-key";
import { ArubaManageDevicesPage } from "@/pages/aruba/manage-devices";
import { ArubaRdvPage } from "@/pages/aruba/rdv";
import { ArubaPlaceholder } from "@/pages/aruba/aruba-placeholder";
import { ArubaGroupManagementPage } from "@/pages/aruba/group-management";
import { ArubaFirmwareUpdatePage } from "@/pages/aruba/firmware-update";
import { ArubaOnboardingPage } from "@/pages/aruba/onboarding";
import { ArubaUserCreationPage } from "@/pages/aruba/user-creation";
import { ArubaSitesPage } from "@/pages/aruba/sites";
import { ArubaWlansPage } from "@/pages/aruba/wlans";
import { CompliancePage } from "@/pages/compliance";
import { ComplianceDiffPage } from "@/pages/compliance-diff";
import { AuditArchivesPage } from "@/pages/audit-archives";
import { AuditLogPage } from "@/pages/audit-log";
import { AgentHistoryPage } from "@/pages/agent";
import { DiagnosePage } from "@/pages/diagnose";
import { CloseoutPage } from "@/pages/closeout";
import { WatchPage } from "@/pages/watch";
import { WorkflowsPage } from "@/pages/workflows";
import { MonitorPage } from "@/pages/monitor";
import { DevicesPage } from "@/pages/devices";
import { UsersPage } from "@/pages/users";
import { HelpPage } from "@/pages/help";
import { ProfilePage } from "./pages/profile";
import { ConfigGeneratorPage } from "@/pages/config-generator";
import { ConfigurationGeneratorPage } from "@/pages/configuration-generator";
import { SwitchPage } from "@/pages/switch";
import { TopologyPage } from "@/pages/topology";
import { WorkbookPage } from "@/pages/workbook";
import { WorkbookDetailPage } from "@/features/workbook/pages/detail";
import { WorkbookEditPage } from "@/features/workbook/pages/edit";
import { WorkbookListPage } from "@/features/workbook/pages/list";
import { WorkbookNewPage } from "@/features/workbook/pages/new";
import { SitesIndexPage } from "@/pages/sites";
import { SiteOverviewPage } from "@/pages/sites/dashboard";
import { ArubaRouteGate } from "@/components/app/aruba-route-gate";
import { RouteErrorBoundary } from "@/components/app/route-error-boundary";

export const router = createBrowserRouter([
  // Public routes — no chrome, no auth required.
  {
    element: (
      <PublicRoute>
        <AuthLayout />
      </PublicRoute>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      { path: "login", element: <LoginPage /> },
      { path: "forgot", element: <ForgotPasswordPage /> },
    ],
  },

  // Authenticated app — everything else.
  {
    path: "/",
    element: (
      <ProtectedRoute>
        <AppShell />
      </ProtectedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, element: <OverviewPage /> },
      { path: "design-system", element: <DesignSystemPage /> },

      // Vendor modules — placeholder until each is migrated.
      { path: "devices", element: <DevicesPage /> },
      { path: "topology", element: <TopologyPage /> },
      { path: "sites", element: <SitesIndexPage /> },
      { path: "sites/:slug", element: <SiteOverviewPage /> },
      {
        path: "meraki",
        element: (
          <PlaceholderPage
            title="Meraki"
            hint="Vendor hub — pick a workflow under /meraki/provisioning."
          />
        ),
      },
      { path: "meraki/provisioning", element: <MerakiWorkflowPicker /> },
      { path: "meraki/keys", element: <SetMerakiKeyPage /> },
      { path: "meraki/network-backup", element: <MerakiDebrandingPage /> },
      { path: "meraki/post-signoff", element: <MerakiPostSignoffPage /> },
      { path: "meraki/api-suite", element: <MerakiApiSuitePage /> },
      { path: "meraki/update-devices", element: <MerakiUpdateDevicesPage /> },
      { path: "meraki/switch-ports", element: <MerakiSwitchPortsPage /> },
      { path: "meraki/onboarding", element: <MerakiOnboardingPage /> },
      {
        path: "meraki/provisioning/run/:slug",
        element: <MerakiProvisioningRunner />,
      },
      {
        element: <ArubaRouteGate />,
        children: [
          {
            path: "aruba",
            element: (
              <ArubaPlaceholder
                title="Aruba"
                hint="Vendor hub — pick a sub-page from the strip above."
              />
            ),
          },
          { path: "aruba/keys", element: <SetArubaKeyPage /> },
          { path: "aruba/devices", element: <ArubaManageDevicesPage /> },
          { path: "aruba/sites", element: <ArubaSitesPage /> },
          { path: "aruba/wlans", element: <ArubaWlansPage /> },
          { path: "aruba/rdv", element: <ArubaRdvPage /> },
          { path: "aruba/firmware", element: <ArubaFirmwareUpdatePage /> },
          { path: "aruba/groups", element: <ArubaGroupManagementPage /> },
          { path: "aruba/onboarding", element: <ArubaOnboardingPage /> },
          { path: "aruba/users", element: <ArubaUserCreationPage /> },
        ],
      },
      { path: "shasta", element: <PlaceholderPage title="Shasta" /> },

      // Automation
      { path: "config-generator", element: <ConfigGeneratorPage /> },
      { path: "configuration-generator", element: <ConfigurationGeneratorPage /> },
      { path: "switch", element: <SwitchPage /> },
      { path: "workflows", element: <WorkflowsPage /> },
      {
        path: "workbook",
        element: <WorkbookPage />,
        children: [
          { index: true, element: <WorkbookListPage /> },
          { path: "new", element: <WorkbookNewPage /> },
          { path: ":workbookId", element: <WorkbookDetailPage /> },
          { path: ":workbookId/edit", element: <WorkbookEditPage /> },
        ],
      },
      { path: "monitor", element: <MonitorPage /> },
      { path: "compliance", element: <CompliancePage /> },
      { path: "compliance/diff/:aId/:bId", element: <ComplianceDiffPage /> },

      // AI
      { path: "diagnose", element: <DiagnosePage /> },
      { path: "closeout", element: <CloseoutPage /> },
      { path: "watch", element: <WatchPage /> },

      // Admin
      { path: "audit", element: <AuditLogPage /> },
      { path: "audit/archives", element: <AuditArchivesPage /> },
      { path: "agent", element: <AgentHistoryPage /> },
      { path: "users", element: <UsersPage /> },
      { path: "help", element: <HelpPage /> },
      { path: "profile", element: <ProfilePage /> },

      { path: "*", element: <NotFoundPage /> },
    ],
  },
]);
